/**
 * Pure decision logic for the router-wide error boundary (see `route-error.tsx`).
 *
 * Kept in its own dependency-free module so the branch logic is unit-testable without pulling in
 * `./main` (the tRPC client / persister, whose import isn't node-safe) or a router/DOM. The effect
 * and fallback UI this drives are covered by the redirect e2e tests.
 */

// The route-policy prefix sets are the SAME ones the proactive `_app` layout guard uses (route-access) —
// shared so the reactive boundary net and the guard can never drift apart.
import { AUTH_REQUIRED_ROUTE_PREFIXES, TEST_GATED_ROUTE_PREFIXES } from './route-access'

// The cached `getUser` shape the classifier reads (authOptional → null when logged out).
export interface RouteErrorUser {
  type?: string
  testPassed?: boolean | null
  testPassedValues?: boolean | null
  testPassedPersonality?: boolean | null
}

/**
 * Given the active route, the cached user and the thrown message, decide whether the boundary should
 * redirect to login (`isAuth`) or to the unfinished test (`isTestIncomplete`).
 *
 *  - No cached user on an auth-required route → auth/expired (key off the absent session, NOT the error
 *    code: a DEFERRED-streamed query's rejection has its code REDACTED in production).
 *  - A logged-in applicant with the test still pending, on a test-gated route → resume the test (same
 *    reason: the streamed message is often redacted). An explicit TEST_INCOMPLETE message also counts.
 */
export function classifyRouteError(input: {
  routeId: string
  cachedUser: RouteErrorUser | null
  message?: string | undefined
}): { isAuth: boolean; isTestIncomplete: boolean } {
  const { routeId, cachedUser, message } = input
  const hasUser = cachedUser != null
  const needsAuth = AUTH_REQUIRED_ROUTE_PREFIXES.some((p) => routeId.startsWith(p))
  const isAuth = !hasUser && needsAuth
  const applicantTestPending =
    cachedUser?.type === 'applicant' &&
    !(cachedUser.testPassed && cachedUser.testPassedValues && cachedUser.testPassedPersonality)
  const onTestGatedRoute = TEST_GATED_ROUTE_PREFIXES.some((p) => routeId.startsWith(p))
  const isTestIncomplete = message === 'TEST_INCOMPLETE' || (applicantTestPending && onTestGatedRoute)
  return { isAuth, isTestIncomplete }
}
